import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { learnAssetUrl } from "./media/learnAssets";

const REVEAL_KEY = "pixley-learn-cloud-reveal";

export const PixleyCloudTiming = {
  coverDuration: (reduce) => (reduce ? 0.16 : 0.66),
  revealDelay: (reduce) => (reduce ? 0.04 : 0.28),
  revealDuration: (reduce) => (reduce ? 0.18 : 0.88),
};

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
  );
}

function markRevealPending() {
  try {
    sessionStorage.setItem(REVEAL_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

function consumeRevealPending() {
  try {
    const raw = sessionStorage.getItem(REVEAL_KEY);
    if (!raw) return false;
    sessionStorage.removeItem(REVEAL_KEY);
    return Date.now() - Number(raw) < 4000;
  } catch {
    return false;
  }
}

const CloudNavContext = createContext(null);

export function useLearnCloudNavigate() {
  const ctx = useContext(CloudNavContext);
  const navigate = useNavigate();
  if (ctx) return ctx;
  return {
    navigateWithCloud: (to) => navigate(to),
    covered: false,
    blocking: false,
  };
}

/** Optional: pages outside the provider can still request a destination reveal. */
export function requestLearnCloudReveal() {
  markRevealPending();
}

/**
 * Visual overlay matching iOS PixleyCloudTransitionOverlay.
 * `covered` true = banks closed over the screen.
 */
export function PixleyCloudTransitionOverlay({ covered, reduceMotion }) {
  const reduce = reduceMotion ?? prefersReducedMotion();

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99990,
        pointerEvents: covered ? "auto" : "none",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgb(222, 245, 255)",
          opacity: covered ? 1 : 0,
          transition: reduce
            ? `opacity ${PixleyCloudTiming.revealDuration(true)}s ease-out`
            : `opacity ${PixleyCloudTiming.coverDuration(false)}s ease-in-out`,
        }}
      />
      <img
        src={learnAssetUrl("pixley_cloud_transition_top")}
        alt=""
        draggable={false}
        style={{
          position: "absolute",
          left: 0,
          width: "100%",
          height: "82%",
          objectFit: "cover",
          objectPosition: "center bottom",
          top: covered ? 0 : "-82%",
          transform: covered || reduce ? "none" : "scale(1.025)",
          transition: reduce
            ? `top ${PixleyCloudTiming.revealDuration(true)}s ease-out, opacity ${PixleyCloudTiming.revealDuration(true)}s ease-out`
            : covered
              ? `top ${PixleyCloudTiming.coverDuration(false)}s ease-in-out`
              : `top ${PixleyCloudTiming.revealDuration(false)}s cubic-bezier(0.22, 1.2, 0.36, 1)`,
          opacity: reduce && !covered ? 0 : 1,
          filter: "drop-shadow(0 10px 18px rgba(0,100,200,0.10))",
        }}
      />
      <img
        src={learnAssetUrl("pixley_cloud_transition_bottom")}
        alt=""
        draggable={false}
        style={{
          position: "absolute",
          left: 0,
          width: "100%",
          height: "82%",
          objectFit: "cover",
          objectPosition: "center top",
          bottom: covered ? 0 : "-82%",
          transform: covered || reduce ? "none" : "scale(1.025)",
          transition: reduce
            ? `bottom ${PixleyCloudTiming.revealDuration(true)}s ease-out, opacity ${PixleyCloudTiming.revealDuration(true)}s ease-out`
            : covered
              ? `bottom ${PixleyCloudTiming.coverDuration(false)}s ease-in-out`
              : `bottom ${PixleyCloudTiming.revealDuration(false)}s cubic-bezier(0.22, 1.2, 0.36, 1)`,
          opacity: reduce && !covered ? 0 : 1,
          filter: "drop-shadow(0 -10px 18px rgba(0,100,200,0.10))",
        }}
      />
    </div>
  );
}

function peekRevealPending() {
  try {
    const raw = sessionStorage.getItem(REVEAL_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < 4000;
  } catch {
    return false;
  }
}

/**
 * Provider for Learn route trees: cover → navigate → destination reveals.
 */
export function LearnCloudTransitionProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [covered, setCovered] = useState(() => peekRevealPending());
  const [blocking, setBlocking] = useState(() => peekRevealPending());
  const timers = useRef([]);
  const inFlight = useRef(false);

  const clearTimers = () => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  };

  const navigateWithCloud = useCallback(
    (to) => {
      if (inFlight.current) return;
      inFlight.current = true;
      const reduce = prefersReducedMotion();
      setBlocking(true);
      setCovered(true);
      markRevealPending();

      const coverMs = PixleyCloudTiming.coverDuration(reduce) * 1000;
      timers.current.push(
        setTimeout(() => {
          try {
            navigate(to);
          } catch {
            // Navigation failed — still clear cover.
          }
          timers.current.push(
            setTimeout(() => {
              setCovered(false);
              setBlocking(false);
              inFlight.current = false;
            }, 220),
          );
        }, coverMs),
      );
    },
    [navigate],
  );

  // Never leave the child stuck under a closed cloud bank.
  useEffect(() => {
    if (!covered) return undefined;
    const t = setTimeout(() => {
      setCovered(false);
      setBlocking(false);
      inFlight.current = false;
    }, 2200);
    return () => clearTimeout(t);
  }, [covered]);

  // Destination reveal when arriving with a pending flag.
  useEffect(() => {
    if (!consumeRevealPending()) return undefined;
    const reduce = prefersReducedMotion();
    setCovered(true);
    setBlocking(true);
    const delayMs = PixleyCloudTiming.revealDelay(reduce) * 1000;
    const revealMs = PixleyCloudTiming.revealDuration(reduce) * 1000;
    const t1 = setTimeout(() => setCovered(false), delayMs);
    const t2 = setTimeout(() => {
      setBlocking(false);
      inFlight.current = false;
    }, delayMs + revealMs);
    timers.current.push(t1, t2);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [location.pathname]);

  useEffect(() => () => clearTimers(), []);

  const value = useMemo(
    () => ({ navigateWithCloud, covered, blocking }),
    [navigateWithCloud, covered, blocking],
  );

  return (
    <CloudNavContext.Provider value={value}>
      {children}
      <PixleyCloudTransitionOverlay covered={covered} />
    </CloudNavContext.Provider>
  );
}

export default PixleyCloudTransitionOverlay;
